import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { _SalesOrderEvent, _single } from "../../../../../core/services/dataBean/EntityCatalog";
import useDataBean from "../../../../../core/services/dataBean/useDataBean";
import { AccountantYear } from "../../../../Talentohumano/GestionTH/EmployeeContract/ProductionPlan/AccountantYear";
import { BasicButton } from "../../../../theme/Component/BasicButton";
import { BasicPage } from "../../../../theme/Component/BasicPage";
import { BasicTable, MASK_DATE_YYYYMMDD_HHMM } from "../../../../theme/Component/BasicTable";
import { ListMenu } from "../../../../theme/Component/ListMenu";
import { Modal } from "../../../../theme/Component/Modal";
import { ModalConfirm } from "../../../../theme/Component/ModalConfirm";
import useModal, { MODAL_LG, MODAL_md } from "../../../../theme/Component/hooks/useModal";
import usePaginator from "../../../../theme/Component/usePaginator";
import { EditorSalesOrder } from "../EditorSalesOrder";
import { SalesOrderOperationEditor } from "./SalesOrderOperationEditor";

interface PropsComponent {
    isModal?: boolean;
    idSalesOrder?: number;
    salesOrderDate?: any;
}

type FilterProps = {
    Year: number;
    IDSalesOrder: number;
    State: number;
    RegexStr: string;
}

const RECORDS_PER_PAGE = 15;

export const SalesOrderEvent = ({ isModal = false, idSalesOrder, salesOrderDate }: PropsComponent) => {
    const {
        instance,
        bean,
        setBean,
        lista,
        setLista,
        addRequiredFields,
        validateForm,
        isRequired,
        isMissing,
    } = useDataBean(_SalesOrderEvent);
    const paginator = usePaginator();
    const [form, setForm] = useState<Partial<FilterProps>>({
        Year: _single.getCurrenYear(),
        RegexStr: "",
    });

    const modalCreate = useModal("Agregar evento de oportunidad de venta", MODAL_LG);
    const modalEdit = useModal("Editar evento de oportunidad de venta", MODAL_LG);
    const modalDelete = useModal("Eliminar evento de oportunidad de venta", MODAL_md);

    useEffect(() => {
        addRequiredFields(
            isModal
                ? ["State", "Observations"]
                : ["IDSalesOrder", "SalesOrderDate", "State", "Observations"]
        );
    }, [isModal]);

    useEffect(() => {
        getLista();
    }, [paginator.page, isModal, idSalesOrder, salesOrderDate]);

    const getLista = () => {
        if (isModal) {
            if (idSalesOrder == null || salesOrderDate == null) {
                setLista([]);
                return;
            }

            instance.getSalesOrderEventCatalogBySalesOrder(idSalesOrder, salesOrderDate).then((resp: any) => {
                setLista(resp ?? []);
            }).catch((err: any) => {
                toast.error(err);
            });
            return;
        }

        if (form.Year == null) {
            toast.error("La vigencia es obligatoria");
            return;
        }

        instance.getSalesOrderEventCatalog(
            form.Year,
            form.IDSalesOrder,
            form.State,
            form.RegexStr,
            paginator.page,
            RECORDS_PER_PAGE
        ).then((resp: any) => {
            paginator.setPages(resp?.NumberOfPages ?? 0);
            paginator.setRecords((resp?.NumberOfPages ?? 0) * RECORDS_PER_PAGE);
            paginator.setRegiPerPage(RECORDS_PER_PAGE);
            setLista(resp?.List ?? []);
        }).catch((err: any) => {
            toast.error(err);
        });
    };

    const search = () => {
        if (form.Year == null) {
            toast.error("La vigencia es obligatoria");
            return;
        }

        if (paginator.page !== 1) {
            paginator.setPage(1);
        } else {
            getLista();
        }
    };

    const openCreate = () => {
        if (isModal && (idSalesOrder == null || salesOrderDate == null)) {
            toast.error("No se recibió la oportunidad de venta");
            return;
        }

        setBean({
            IDSalesOrder: isModal ? idSalesOrder : null,
            SalesOrderDate: isModal ? salesOrderDate : null,
            State: null,
            Observations: "",
        });
        modalCreate.open();
    };

    const createItem = () => {
        if (!validateForm()) {
            toast.error("Faltan campos obligatorios por completar");
            return;
        }

        instance.addSalesOrderEvent(
            bean.IDSalesOrder,
            bean.SalesOrderDate,
            bean.State,
            bean.Observations,
            _single.getAccountID()
        ).then(() => {
            toast.success("Evento agregado correctamente");
            modalCreate.close();
            getLista();
        }).catch((err: any) => {
            toast.error(err);
        });
    };

    const editItem = () => {
        if (!validateForm()) {
            toast.error("Faltan campos obligatorios por completar");
            return;
        }

        instance.updateSalesOrderEvent(bean).then(() => {
            toast.success("Evento actualizado correctamente");
            modalEdit.close();
            getLista();
        }).catch((err: any) => {
            toast.error(err);
        });
    };

    const deleteItem = () => {
        instance.deleteSalesOrderEvent(bean.IDSalesOrderEvent).then(() => {
            toast.success("Evento eliminado correctamente");
            modalDelete.close();
            getLista();
        }).catch((err: any) => {
            toast.error(err);
        });
    };

    const catalogHead = [
        { title: "ID", property: "IDSalesOrderEvent", visible: true },
        { title: "Oportunidad / Evento", property: "IDSalesOrder", visible: true },
        { title: "Fecha Publicación", property: "SalesOrderDate", visible: true, mask: MASK_DATE_YYYYMMDD_HHMM },
        { title: "Fecha de Cierre", property: "CustomerDefeatDate", visible: true, mask: MASK_DATE_YYYYMMDD_HHMM },
        { title: "Estado Oportunidad", property: "SalesOrderStateName", visible: true },
        { title: "Cliente", property: "AccountCustomerName", visible: true },
        { title: "Referencia Cliente", property: "SalesOrderReference", visible: true },
        { title: "Descripción Oportunidad", property: "SalesOrderDescription", visible: true },
        { title: "Tipo de Evento", property: "StateName", visible: true },
        { title: "Descripción Evento", property: "Observations", visible: true },
        { title: "Empleado", property: "AccountName", visible: true },
        { title: "Fecha Evento", property: "Since", visible: true, mask: MASK_DATE_YYYYMMDD_HHMM },
    ];

    const salesOrderHead = [
        { title: "ID", property: "IDSalesOrderEvent", visible: true },
        { title: "Tipo de Evento", property: "StateName", visible: true },
        { title: "Observaciones", property: "Observations", visible: true },
        { title: "Empleado", property: "AccountName", visible: true },
        { title: "Fecha de Evento", property: "Since", visible: true, mask: MASK_DATE_YYYYMMDD_HHMM },
    ];

    const rowButtons = (
        <ListMenu listaBotones={[
            {
                titulo: "Editar",
                icono: "ri-edit-2-line",
                visible: _single.canEdit(),
                evento: () => modalEdit.open(),
            },
            {
                titulo: "Eliminar",
                icono: "ri-delete-bin-line",
                visible: _single.canDelete(),
                evento: () => modalDelete.open(),
            },
        ]} />
    );

    const eventFields = (isCreate: boolean) => (
        <div className="row">
            {isCreate && !isModal && (
                <div className={`col-md-6`}>
                    <label className={isMissing("IDSalesOrder") || isMissing("SalesOrderDate") ? "has-error" : ""}>
                        Oportunidad de Venta {isRequired("IDSalesOrder") ? <span className="text-danger">*</span> : ""}
                    </label>
                    <EditorSalesOrder
                        label=" "
                        idSelector={bean.IDSalesOrder}
                        salesOrderDate={bean.SalesOrderDate}
                        render={bean.SalesOrderDisplay}
                        onChange={(value) => setBean({
                            ...bean,
                            IDSalesOrder: value.IDSalesOrder,
                            SalesOrderDate: value.SalesOrderDate,
                            SalesOrderDisplay: `${value.IDSalesOrder} - ${value.SalesOrderDate ?? ""}${value.AccountCustomerName ? ` - ${value.AccountCustomerName}` : ""}`,
                        })}
                    />
                </div>
            )}
            <div className={`col-md-${isModal ? 12 : 6}`}>
                <label className={isMissing("State") ? "has-error" : ""}>
                    Tipo de Evento {isRequired("State") ? <span className="text-danger">*</span> : ""}
                </label>
                <SalesOrderOperationEditor
                    label=" "
                    idSelector={bean.State}
                    onChange={(value) => setBean({ ...bean, State: value })}
                />
            </div>
            <div className="col-md-12">
                <label className={isMissing("Observations") ? "has-error" : ""}>
                    Descripción Evento {isRequired("Observations") ? <span className="text-danger">*</span> : ""}
                </label>
                <textarea
                    className="form-control"
                    value={bean.Observations ?? ""}
                    onChange={(e) => setBean({ ...bean, Observations: e.target.value })}
                />
            </div>
        </div>
    );

    const content = (
        <div className="row">
            {!isModal && (
                <>
                    <div className="col-md-6">
                        <label className={form.Year == null ? "has-error" : ""}>Vigencia</label>
                        <AccountantYear
                            label=" "
                            idSelector={form.Year}
                            onChange={(value: number) => setForm({ ...form, Year: value })}
                        />
                    </div>
                    <div className="col-md-6">
                        <label>Número de Orden</label>
                        <input
                            className="form-control"
                            type="number"
                            value={form.IDSalesOrder ?? ""}
                            onChange={(e) => setForm({
                                ...form,
                                IDSalesOrder: e.target.value === "" ? null : Number(e.target.value),
                            })}
                        />
                    </div>
                    <div className="col-md-6">
                        <SalesOrderOperationEditor
                            idSelector={form.State}
                            onChange={(value) => setForm({ ...form, State: value })}
                        />
                    </div>
                    <div className="col-md-6">
                        <label>Búsqueda por Texto</label>
                        <input
                            className="form-control"
                            type="text"
                            value={form.RegexStr ?? ""}
                            onChange={(e) => setForm({ ...form, RegexStr: e.target.value })}
                            onKeyDown={(e) => e.key === "Enter" && search()}
                        />
                    </div>
                    <div className="col-md-12 mt-4 p-2">
                        <BasicButton eventClick={search} icon="ri-search-line">Buscar</BasicButton>
                    </div>
                </>
            )}

            <div className="col-md-12">
                <BasicTable
                    body={lista}
                    head={isModal ? salesOrderHead : catalogHead}
                    rowSet={setBean}
                    listButtons={rowButtons}
                    iconButtons={[{
                        nombre: "Agregar evento",
                        visible: _single.canCreate(),
                        icono: "ri-file-add-line",
                        evento: openCreate,
                    }]}
                    records={isModal ? undefined : paginator}
                    customRows={isModal ? 15 : undefined}
                />

                <Modal modal={modalCreate.modal} updateModal={modalCreate.setModal} eventModal={createItem}>
                    {eventFields(true)}
                </Modal>

                <Modal modal={modalEdit.modal} updateModal={modalEdit.setModal} eventModal={editItem}>
                    {eventFields(false)}
                </Modal>

                <ModalConfirm modal={modalDelete.modal} updateModal={modalDelete.setModal} eventModal={deleteItem}>
                    <strong>Eliminar evento {bean.IDSalesOrderEvent}</strong>
                </ModalConfirm>
            </div>
        </div>
    );

    return isModal ? content : (
        <BasicPage titulo="Eventos de Oportunidades de Venta">
            {content}
        </BasicPage>
    );
};
